import React, { Suspense } from "react";
import { Spinner } from "react-bootstrap";
import { AllowedAccess } from "react-permission-role";
import { Navigate, createBrowserRouter } from "react-router-dom";

import App from "App";
import { PERMISSIONS } from "features/constants/permissions";

// Auth components — small, keep eager for instant login page
import CardConfirmMail from "components/authentication/card/ConfirmMail";
import CardForgetPassword from "components/authentication/card/ForgetPassword";
import CardLockScreen from "components/authentication/card/LockScreen";
import CardLogin from "components/authentication/card/Login";
import CardLogout from "components/authentication/card/Logout";
import CardPasswordReset from "components/authentication/card/PasswordReset";
import CardRegistration from "components/authentication/card/Registration";
import TechnicalIssue from "components/authentication/card/TechnicalIssue";
import SimpleConfirmMail from "components/authentication/simple/ConfirmMail";
import SimpleForgetPassword from "components/authentication/simple/ForgetPassword";
import SimpleLockScreen from "components/authentication/simple/LockScreen";
import SimpleLogin from "components/authentication/simple/Login";
import SimpleLogout from "components/authentication/simple/Logout";
import SimplePasswordReset from "components/authentication/simple/PasswordReset";
import SimpleRegistration from "components/authentication/simple/Registration";
import WizardAuth from "components/authentication/wizard/WizardAuth";
// Error pages — small, keep eager
import Error401 from "components/errors/Error401";
import Error404 from "components/errors/Error404";
import Error500 from "components/errors/Error500";

// Layouts — lazy loaded (MainLayout pulls in heavy dependencies)
const AuthSimpleLayout = React.lazy(() =>
  import("../layouts/AuthSimpleLayout")
);
const ErrorLayout = React.lazy(() => import("../layouts/ErrorLayout"));
const MainLayout = React.lazy(() => import("../layouts/MainLayout"));

// Dashboard & SEDRA3 — lazy loaded (heavy components)
const MainDashboard = React.lazy(() => import("layouts/MainDashboard"));
const CallsForApplicationPage = React.lazy(() =>
  import("layouts/CallsForApplicationComponent")
);
const CallApplication = React.lazy(() =>
  import("components/dashboards/SEDRA3/CallApplicationTable")
);
const ConfirmEmailPage = React.lazy(() =>
  import("components/dashboards/SEDRA3/ConfirmMailPage")
);
const FeedbackForm = React.lazy(() =>
  import("components/dashboards/SEDRA3/FeedbackForm")
);
const FeedbackManagement = React.lazy(() =>
  import("components/dashboards/SEDRA3/FeedbackManagement")
);
const FileTemplateManagement = React.lazy(() =>
  import("components/dashboards/SEDRA3/FileTemplateManagement")
);
const SelfDeclarationTemplateFileUpload = React.lazy(() =>
  import("components/dashboards/SEDRA3/SelfDeclarationTemplateFileUpload")
);
const Profile = React.lazy(() =>
  import("components/pages/user/profile/Profile")
);
const UserManagement = React.lazy(() =>
  import("components/pages/user/profile/UserManagement")
);

import paths, { rootPaths } from "./paths";

// Suspense wrapper for lazy components
const Lazy = ({ children }) => (
  <Suspense
    fallback={
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <Spinner animation="border" />
      </div>
    }
  >
    {children}
  </Suspense>
);

const routes = [
  {
    element: <App />,
    children: [
      {
        path: "/authentication/card/confirm-email/:token",
        element: (
          <Lazy>
            <ConfirmEmailPage />
          </Lazy>
        ),
      },
      {
        path: rootPaths.errorsRoot,
        element: (
          <Lazy>
            <ErrorLayout />
          </Lazy>
        ),
        children: [
          {
            path: paths.error404,
            element: <Error404 />,
          },
          {
            path: paths.error500,
            element: <Error500 />,
          },
          {
            path: paths.error401,
            element: <Error401 />,
          },
        ],
      },
      {
        path: rootPaths.authRoot,
        children: [
          {
            path: rootPaths.authSimpleRoot,
            element: (
              <Lazy>
                <AuthSimpleLayout />
              </Lazy>
            ),
            children: [
              {
                path: paths.simpleLogin,
                element: <SimpleLogin />,
              },
              {
                path: paths.simpleRegister,
                element: <SimpleRegistration />,
              },
              {
                path: paths.simpleLogout,
                element: <SimpleLogout />,
              },
              {
                path: paths.simpleForgotPassword,
                element: <SimpleForgetPassword />,
              },
              {
                path: paths.simpleResetPassword,
                element: <SimplePasswordReset />,
              },
              {
                path: paths.simpleConfirmMail,
                element: <SimpleConfirmMail />,
              },
              {
                path: paths.simpleLockScreen,
                element: <SimpleLockScreen />,
              },
            ],
          },
          {
            path: rootPaths.authCardRoot,
            children: [
              {
                path: paths.cardLogin,
                element: <CardLogin />,
              },
              {
                path: paths.cardConfirmMail,
                element: <CardConfirmMail />,
              },
              {
                path: paths.cardTechnicalIssue,
                element: <TechnicalIssue />,
              },
              {
                path: paths.cardRegister,
                element: <CardRegistration />,
              },
              {
                path: paths.cardLogout,
                element: <CardLogout />,
              },
              {
                path: paths.cardForgotPassword,
                element: <CardForgetPassword />,
              },
              {
                path: paths.cardResetPassword,
                element: <CardPasswordReset />,
              },
              {
                path: paths.cardLockScreen,
                element: <CardLockScreen />,
              },
            ],
          },

          {
            path: paths.authWizard,
            element: <WizardAuth />,
          },
        ],
      },
      {
        path: "/",
        element: (
          <Lazy>
            <MainLayout />
          </Lazy>
        ),
        children: [
          {
            index: true, // Default route under MainLayout
            element: <Navigate to={paths.mainDashboard} replace />,
          },
          {
            path: rootPaths.dashboardRoot,
            children: [
              {
                path: paths.mainDashboard,
                element: (
                  <Lazy>
                    <MainDashboard />
                  </Lazy>
                ),
              },
            ],
          },
          {
            path: rootPaths.userRoot,
            children: [
              {
                path: paths.userProfile,
                element: (
                  <Lazy>
                    <Profile />
                  </Lazy>
                ),
              },
              {
                path: paths.userManagement,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.USERS_MANAGEMENT]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <UserManagement />
                    </Lazy>
                  </AllowedAccess>
                ),
              },

              {
                path: paths.callApplication,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.CALLS_FOR_APPLICATIONS]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <CallApplication />
                    </Lazy>
                  </AllowedAccess>
                ),
              },
              {
                path: paths.feedback,
                element: (
                  <Lazy>
                    <FeedbackForm />
                  </Lazy>
                ),
              },
              {
                path: paths.feedbackManagement,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.FEEDBACK_MANAGEMENT]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <FeedbackManagement />
                    </Lazy>
                  </AllowedAccess>
                ),
              },

              {
                path: paths.selfDeclarationTemplateFileUpload,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.TEMPLATE_FILES_MANAGEMENT]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <SelfDeclarationTemplateFileUpload />
                    </Lazy>
                  </AllowedAccess>
                ),
              },

              {
                path: paths.fileTemplateManagement,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.TEMPLATE_FILES_MANAGEMENT]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <FileTemplateManagement />
                    </Lazy>
                  </AllowedAccess>
                ),
              },
            ],
          },
          {
            path: rootPaths.callApplicationWidget,
            children: [
              {
                path: paths.callsForApplicaionsWidgets,
                element: (
                  <AllowedAccess
                    permissions={[PERMISSIONS.VIEW_APPLICATIONS]}
                    renderAuthFailed={<Error401 />}
                  >
                    <Lazy>
                      <CallsForApplicationPage />
                    </Lazy>
                    ,
                  </AllowedAccess>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "*",
        element: <Navigate to={paths.error404} replace />,
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  basename: import.meta.env.BASE_URL,
});

export default routes;
